import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { useState, useEffect } from 'react';
import defaultBuildInfo from './build-info.json';

export interface BuildInfo {
  commitSha: string;
  commitShortSha: string;
  branch: string;
  pipelineId?: string;
  pipelineIid?: string;
  pipelineUrl?: string;
  projectUrl: string;
  buildTime: string;
  imageTag: string;
}

export interface KubernetesInfo {
  podName: string;
  podNamespace: string;
  podIp: string;
  nodeName: string;
  clusterName: string;
  isKubernetes: boolean;
}

export interface RuntimeInfo {
  nodeVersion: string;
  uptimeSeconds: number;
  memory: {
    heapUsedMb: number;
    heapTotalMb: number;
    rssMb: number;
    externalMb: number;
  };
  os: {
    platform: string;
    arch: string;
    release: string;
    cpus: number;
    totalMemoryGb: number;
    freeMemoryGb: number;
  };
  environment: string;
}

export interface SubsystemsInfo {
  database: {
    client: string;
    status: 'connected' | 'error' | 'unknown';
    latencyMs?: number;
    error?: string;
  };
  redis: {
    status: 'configured' | 'unconfigured' | 'unknown';
    url?: string;
  };
  techdocs: {
    publisherType: string;
    s3Endpoint?: string;
    s3Bucket?: string;
  };
  auth: {
    environment: string;
    providers: string[];
  };
}

export interface SystemInfoResponse {
  build: BuildInfo;
  kubernetes: KubernetesInfo;
  runtime: RuntimeInfo;
  subsystems: SubsystemsInfo;
}

export const staticBuildInfo: BuildInfo = defaultBuildInfo as BuildInfo;

export function useSystemInfo() {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [data, setData] = useState<SystemInfoResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function load() {
      try {
        setLoading(true);
        const baseUrl = await discoveryApi.getBaseUrl('system-info');
        const res = await fetchApi.fetch(`${baseUrl}/info`);
        if (!res.ok) {
          throw new Error(`Failed to fetch system info: ${res.statusText}`);
        }
        const json: SystemInfoResponse = await res.json();
        if (isMounted) {
          setData(json);
          setError(null);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err);
          // Fallback to static build info if backend is unreachable
          setData({
            build: staticBuildInfo,
            kubernetes: {
              podName: 'unknown',
              podNamespace: 'unknown',
              podIp: '127.0.0.1',
              nodeName: 'local',
              clusterName: 'unknown',
              isKubernetes: false,
            },
            runtime: {
              nodeVersion: 'unknown',
              uptimeSeconds: 0,
              memory: {
                heapUsedMb: 0,
                heapTotalMb: 0,
                rssMb: 0,
                externalMb: 0,
              },
              os: {
                platform: 'unknown',
                arch: 'unknown',
                release: '',
                cpus: 0,
                totalMemoryGb: 0,
                freeMemoryGb: 0,
              },
              environment: 'unknown',
            },
            subsystems: {
              database: { client: 'unknown', status: 'unknown' },
              redis: { status: 'unknown' },
              techdocs: { publisherType: 'unknown' },
              auth: { environment: 'unknown', providers: [] },
            },
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      isMounted = false;
    };
  }, [discoveryApi, fetchApi]);

  return { data, loading, error };
}

export function printDevToolsBanner(info?: BuildInfo) {
  const build = info || staticBuildInfo;
  const bannerStyle =
    'background: #0d1b2a; color: #38bdf8; font-family: monospace; font-size: 13px; font-weight: bold; padding: 6px 10px; border-radius: 4px; border-left: 4px solid #0284c7;';
  const labelStyle = 'color: #94a3b8; font-weight: normal;';
  const valStyle = 'color: #f1f5f9; font-weight: bold;';

  console.groupCollapsed(
    `%c Sulmara Backstage %c ${build.imageTag || build.commitShortSha} `,
    bannerStyle,
    'background: #0284c7; color: #ffffff; font-family: monospace; font-size: 13px; font-weight: bold; padding: 6px 10px; border-radius: 4px;',
  );
  console.log(`%cTag:      %c${build.imageTag}`, labelStyle, valStyle);
  console.log(
    `%cCommit:   %c${build.commitSha} (${build.commitShortSha})`,
    labelStyle,
    valStyle,
  );
  console.log(`%cBranch:   %c${build.branch}`, labelStyle, valStyle);
  if (build.pipelineId)
    console.log(
      `%cPipeline: %c#${build.pipelineIid || build.pipelineId}`,
      labelStyle,
      valStyle,
    );
  console.log(`%cBuilt At: %c${build.buildTime}`, labelStyle, valStyle);
  console.log(`%cGitLab:   %c${build.projectUrl}`, labelStyle, valStyle);
  console.groupEnd();
}
